import CheckCircle from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { Link } from 'react-router-dom';

import { categoryBySide, getSettlementsTabPath } from '../chargeSelection';
import { CHARGES_CELL_SX } from '../chargesGrid';

const chipSx = {
  borderRadius: 0.5,
  display: 'inline-block',
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '18px',
  maxWidth: '100%',
  overflow: 'hidden',
  px: 0.75,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const settledChipSx = (theme: Theme, isApproved: boolean) => {
  const color = isApproved
    ? theme.brandV2.colors.treadGreen
    : theme.brandV2.colors.treadBlue2;

  return {
    ...chipSx,
    alignItems: 'center',
    border: `solid 1px ${color}`,
    color,
    display: 'inline-flex',
    gap: 0.25,
  };
};

const approvedIconSx = { flexShrink: 0, fontSize: '12px' };

interface SettlementNumberChipProps {
  hasUnrejectedLines: boolean;
  hasUnsettledLines?: boolean;
  isApproved?: boolean;
  settlementNumbers: string[];
  side: BillingLineSide;
  sx?: SxProps<Theme>;
}

export default function SettlementNumberChip({
  hasUnrejectedLines,
  hasUnsettledLines = false,
  isApproved = false,
  settlementNumbers,
  side,
  sx,
}: SettlementNumberChipProps) {
  const [settlementNumber] = settlementNumbers;

  return (
    <Box sx={[CHARGES_CELL_SX, ...(Array.isArray(sx) ? sx : [sx])]}>
      {settlementNumbers.length > 1 && (
        <Typography
          component="span"
          data-test-id="settlement-number-chip"
          sx={(theme) => settledChipSx(theme, isApproved)}
        >
          {isApproved && <CheckCircle aria-hidden sx={approvedIconSx} />}
          {t('approvals.approve_charges.settlement_count', {
            count: settlementNumbers.length,
          })}
        </Typography>
      )}

      {settlementNumbers.length === 1 && (
        <Typography
          component={Link}
          data-test-id="settlement-number-chip"
          sx={(theme) => ({
            ...settledChipSx(theme, isApproved),
            textDecoration: 'none',
            '&:hover': { bgcolor: theme.brandV2.colors.treadGray8 },
          })}
          title={
            isApproved ? t('approvals.approve_charges.settlement_approved') : undefined
          }
          to={getSettlementsTabPath(categoryBySide[side], settlementNumber)}
        >
          {isApproved && <CheckCircle aria-hidden sx={approvedIconSx} />}
          {settlementNumber}
        </Typography>
      )}

      {settlementNumbers.length > 0 && hasUnsettledLines && (
        <Typography
          component="span"
          data-test-id="settlement-partly-settled-chip"
          sx={(theme) => ({
            ...chipSx,
            border: `solid 1px ${theme.brandV2.colors.treadGray6}`,
            color: theme.brandV2.colors.treadGray4,
            ml: 0.5,
          })}
        >
          {t('approvals.approve_charges.partly_settled')}
        </Typography>
      )}

      {settlementNumbers.length === 0 && hasUnrejectedLines && (
        <Typography
          component="span"
          data-test-id="settlement-unsettled-chip"
          sx={(theme) => ({
            ...chipSx,
            border: `solid 1px ${theme.brandV2.colors.treadGray6}`,
            color: theme.brandV2.colors.treadGray4,
          })}
        >
          {t('approvals.approve_charges.unsettled')}
        </Typography>
      )}
    </Box>
  );
}
